const _excluded = ["isOpen", "ariaDescribedby", "value", "multiple", "disabled", "error", "size", "name", "valueToString", "placeholder", "multiline", "loading", "loadingSpinnerId", "loadingSpinnerAriaLabel", "onClear", "clearable"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import * as React from "react";
import { ChevronDown, Clear } from "@bolteu/kalep-react-icons";
import { MultiSelectChip } from "../_internal/MultiSelectChip";
import { GhostButton } from "../GhostButton";
import { SpinnerIcon } from "../SpinnerIcon";
import { cx } from "../../helpers/utils/cx";
const sizes = {
  sm: "min-h-9 py-1 pl-3 pr-2 bolt-font-body-s-regular gap-2",
  md: "min-h-12 py-2 pl-3 pr-2 bolt-font-body-m-regular gap-2",
  lg: "min-h-14 py-3 pl-3 pr-2 bolt-font-body-m-regular gap-2"
};
const states = {
  idle: "border-special-nulled focus:border-action-primary",
  disabled: "border-neutral-secondary cursor-not-allowed text-tertiary bg-special-nulled placeholder-tertiary",
  error: "border-danger-primary"
};
export const DEFAULT_LOADING_SPINNER_ARIA_LABEL = "loading results";
const CHIPS_COUNT_SAFEAREA = 24;
const getValueString = (valueToString, value, placeholder) => {
  if (!value) {
    return placeholder;
  }
  if (Array.isArray(value)) {
    return value.length ? value.map(item => valueToString(item)).join(", ") : placeholder;
  }
  return valueToString(value);
};
export const SelectTrigger = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
      isOpen,
      ariaDescribedby,
      value,
      multiple,
      disabled,
      error,
      size = "md",
      name,
      valueToString,
      placeholder,
      multiline,
      loading,
      loadingSpinnerId,
      loadingSpinnerAriaLabel = DEFAULT_LOADING_SPINNER_ARIA_LABEL,
      onClear,
      clearable
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const isValueArray = Array.isArray(value);
  const hasValue = isValueArray ? Boolean(value.length) : Boolean(value);
  const multiChipContainerRef = React.useRef(null);
  const chipRefs = React.useRef({});
  const [maxChipIndex, setMaxChipIndex] = React.useState(isValueArray ? value.length - 1 : -1);
  const [visibleChipOffset, setVisibleChipOffset] = React.useState(0);
  const [chipTransform, setChipTransform] = React.useState(0);
  const hiddenChipCount = isValueArray ? value.length - maxChipIndex - 1 : 0;
  const chipGap = React.useMemo(() => size === "sm" ? 4 : 6, [size]);
  const handleClearButtonClick = React.useCallback(e => {
    onClear?.();
    e.preventDefault();
    e.stopPropagation();
  }, [onClear]);
  if (isValueArray) {
    value.forEach(option => {
      if (option.value && !chipRefs.current[option.value]) {
        chipRefs.current[option.value] = chipRefs.current[option.value] || /*#__PURE__*/React.createRef();
      }
    });
  }
  React.useLayoutEffect(() => {
    if (!multiline && isValueArray && multiChipContainerRef.current) {
      const containerWidth = multiChipContainerRef.current.getBoundingClientRect().width;
      const safetyAreaWidth = CHIPS_COUNT_SAFEAREA + chipGap; // For the +1 indicator

      let chipsFullWidth = 0;
      let chipsVisibleWidth = 0;
      let chipsOverflow = false;
      let maxIndexToShow = 0;
      for (let i = 0; i < value.length; i++) {
        const option = value[i];
        if (option.value && chipRefs.current[option.value]) {
          const chipElement = chipRefs.current[option.value].current;
          if (chipElement) {
            let chipWidth = chipElement.getBoundingClientRect().width;
            if (chipWidth + safetyAreaWidth >= containerWidth) {
              chipWidth = containerWidth - safetyAreaWidth;
              chipElement.style.width = `${chipWidth}px`;
              chipElement.style.overflow = "hidden";
              chipElement.style.textOverflow = "ellipsis";
            }
            const gap = i === 0 ? 0 : chipGap;
            chipsFullWidth += gap + chipWidth;
            if (!chipsOverflow) {
              if (chipsFullWidth + safetyAreaWidth <= containerWidth) {
                chipsVisibleWidth += gap + chipWidth;
                maxIndexToShow = i;
              } else {
                chipsOverflow = true;
              }
            }
          }
        }
      }
      setMaxChipIndex(maxIndexToShow);
      setVisibleChipOffset(chipsVisibleWidth);
      setChipTransform(Math.min(containerWidth - chipsFullWidth, 0));
    }
  }, [value, multiline, isValueArray, isOpen, chipGap]);
  return /*#__PURE__*/React.createElement("button", _extends({
    ref: ref,
    "aria-describedby": ariaDescribedby,
    className: cx("border-solid cursor-pointer",
    // Reset
    "group/trigger", "flex justify-end items-center", "border-2 rounded-md w-full outline-none overflow-hidden", "bg-neutral-secondary text-left", "ease-linear duration-100", "focus-within:bg-special-nulled", hasValue && !disabled ? "text-primary" : "text-secondary", disabled && states.disabled, error && states.error, !disabled && !error && states.idle, sizes[size], !error && isOpen && "border-action-primary bg-special-nulled"),
    disabled: disabled,
    name: name,
    type: "button"
  }, rest), multiple && isValueArray && value.length ? /*#__PURE__*/React.createElement("div", {
    ref: multiChipContainerRef,
    className: cx("relative w-full flex flex-1 transition-transform", !multiline && isOpen ? "overflow-visible" : "overflow-hidden", multiline && "flex-wrap overflow-auto"),
    style: {
      transform: `translateX(${!multiline && isOpen && hiddenChipCount > 0 ? chipTransform : 0}px)`,
      gap: chipGap
    }
  }, value.map((option, i) => /*#__PURE__*/React.createElement(MultiSelectChip, {
    key: option.value,
    ref: option.value ? chipRefs.current[option.value] : undefined,
    label: valueToString(option),
    disabled: disabled,
    hidden: i > maxChipIndex && !multiline && !isOpen,
    shrink: multiline,
    size: size
  })), !multiline && hiddenChipCount > 0 && !isOpen && /*#__PURE__*/React.createElement("div", {
    className: cx("absolute w-6 py-1 text-left top-0", size === "sm" ? "bolt-font-body-xs-regular" : "bolt-font-body-s-regular", disabled ? "text-tertiary" : "text-primary"),
    style: {
      left: visibleChipOffset + chipGap
    }
  }, `+${hiddenChipCount}`)) : /*#__PURE__*/React.createElement("div", {
    className: cx("w-full", !multiline && "whitespace-nowrap overflow-hidden text-ellipsis")
  }, getValueString(valueToString, value, placeholder)), /*#__PURE__*/React.createElement("div", {
    className: cx("flex items-center justify-end flex-shrink-0", size !== "sm" && "gap-1")
  }, !disabled && hasValue && clearable && /*#__PURE__*/React.createElement(GhostButton, {
    onClick: handleClearButtonClick,
    overrideClassName: cx("group/clear", "w-6 flex-shrink-0 mx-1"),
    tabIndex: -1
  }, /*#__PURE__*/React.createElement("div", {
    className: cx(isOpen ? "flex" : "hidden", "group-hover/trigger:flex", "justify-center items-center")
  }, /*#__PURE__*/React.createElement(Clear, {
    "aria-hidden": "true",
    className: cx("text-tertiary group-hover/clear:text-secondary"),
    size: size === "sm" ? "md" : "lg"
  }))), loading ? /*#__PURE__*/React.createElement(SpinnerIcon, {
    id: loadingSpinnerId,
    "aria-label": loadingSpinnerAriaLabel,
    className: cx("flex-none mx-1 animate-spin text-action-secondary"),
    size: size,
    role: "status"
  }) : /*#__PURE__*/React.createElement(ChevronDown, {
    className: cx("flex-none transition-transform mx-1", disabled ? "text-tertiary" : "text-primary", isOpen && "rotate-180"),
    size: size === "sm" ? "md" : "lg"
  })));
});